import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import ScrollToTop from "@/components/site/ScrollToTop";
import Home from "@/pages/Home";
import About from "@/pages/About";
import GoldenVisa from "@/pages/GoldenVisa";
import BusinessSetup from "@/pages/BusinessSetup";
import Relocation from "@/pages/Relocation";
import Contact from "@/pages/Contact";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import CrmLogin from "@/pages/crm/Login";
import AuthCallback from "@/pages/crm/AuthCallback";
import ProtectedRoute from "@/components/crm/ProtectedRoute";
import CrmLayout from "@/components/crm/CrmLayout";
import Dashboard from "@/pages/crm/Dashboard";
import Leads from "@/pages/crm/Leads";
import LeadDetail from "@/pages/crm/LeadDetail";
import Pipeline from "@/pages/crm/Pipeline";
import Tasks from "@/pages/crm/Tasks";
import Search from "@/pages/crm/Search";
import CrmSettings from "@/pages/crm/Settings";
import InsightsAdmin from "@/pages/crm/InsightsAdmin";
import InsightEditor from "@/pages/crm/InsightEditor";

function PublicSite() {
  return (
    <>
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/golden-visa" element={<GoldenVisa />} />
          <Route path="/business-setup" element={<BusinessSetup />} />
          <Route path="/relocation" element={<Relocation />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function AppRouter() {
  const location = useLocation();

  // Handle Emergent auth callback (session_id in URL fragment) — must be processed
  // before ProtectedRoute checks. Only applies to CRM redirects.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* CRM (protected) */}
      <Route path="/crm/login" element={<CrmLogin />} />
      <Route path="/crm" element={<ProtectedRoute><CrmLayout><Dashboard /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/leads" element={<ProtectedRoute><CrmLayout><Leads /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/leads/:id" element={<ProtectedRoute><CrmLayout><LeadDetail /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/pipeline" element={<ProtectedRoute><CrmLayout><Pipeline /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/tasks" element={<ProtectedRoute><CrmLayout><Tasks /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/search" element={<ProtectedRoute><CrmLayout><Search /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/settings" element={<ProtectedRoute><CrmLayout><CrmSettings /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/insights" element={<ProtectedRoute><CrmLayout><InsightsAdmin /></CrmLayout></ProtectedRoute>} />
      <Route path="/crm/insights/:id" element={<ProtectedRoute><CrmLayout><InsightEditor /></CrmLayout></ProtectedRoute>} />

      {/* Public marketing site */}
      <Route path="/*" element={<PublicSite />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <ThemeProvider>
          <AuthProvider>
            <SettingsProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AppRouter />
                <Toaster position="bottom-center" theme="light" />
              </BrowserRouter>
            </SettingsProvider>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
